import { db } from "../db";

/**
 * Gets the current active offline server configuration
 * IMPORTANT: This function needs the PromptusAPIState context to accurately determine online/offline status
 * The api_host in DB might still contain localhost URLs even when in online mode
 * @param {Object} apiContext - Optional PromptusAPIState context for accurate online/offline detection
 * @returns {Promise<{
 *   isOffline: boolean,
 *   activeServer: 'GPU' | 'CPU' | 'Cosy' | 'Cloud' | null,
 *   currentHost: string,
 *   serverUrls: {
 *     gpu: string,
 *     cpu: string,
 *     cosy: string
 *   }
 * }>}
 */
export async function getActiveOfflineMode(apiContext = null) {
    try {
        // Get all relevant settings from IndexedDB
        const settings = await db.settings.toArray();
        
        // Initialize result object
        const result = {
            isOffline: false,
            activeServer: null,
            currentHost: '',
            serverUrls: {
                gpu: 'http://localhost:7111/',
                cpu: 'http://localhost:7112/',
                cosy: 'http://localhost:8190/'
            }
        };
        
        // Process settings
        settings.forEach(setting => {
            switch(setting.key) {
                case 'api_host':
                    result.currentHost = setting.value;
                    break;
                case 'local_gpu':
                    result.serverUrls.gpu = setting.value;
                    break;
                case 'local_cpu':
                    result.serverUrls.cpu = setting.value;
                    break;
                case 'local_cosy':
                    result.serverUrls.cosy = setting.value;
                    break;
            }
        });
        
        // If apiContext is provided, use it for accurate online/offline detection
        if (apiContext) {
            result.isOffline = !apiContext.online;
            
            if (!apiContext.online) {
                // We're in offline mode, determine which server
                if (apiContext.activeGPU) {
                    result.activeServer = 'GPU';
                    result.currentHost = result.serverUrls.gpu;
                } else if (apiContext.activeCPU) {
                    result.activeServer = 'CPU';
                    result.currentHost = result.serverUrls.cpu;
                } else if (apiContext.activeCosy) {
                    result.activeServer = 'Cosy';
                    result.currentHost = result.serverUrls.cosy;
                } else if (result.currentHost && !result.currentHost.includes('api.promptus.ai')) {
                    // Offline but no specific server flag set, try to determine from host
                    if (result.currentHost === result.serverUrls.gpu) {
                        result.activeServer = 'GPU';
                    } else if (result.currentHost === result.serverUrls.cpu) {
                        result.activeServer = 'CPU';
                    } else if (result.currentHost === result.serverUrls.cosy) {
                        result.activeServer = 'Cosy';
                    } else {
                        result.activeServer = 'Custom';
                    }
                } else {
                    result.activeServer = 'Unknown';
                }
            } else {
                // We're in online mode
                result.activeServer = 'Cloud';
                // In online mode, the actual host used is the cloud API, not what's stored in api_host
                result.currentHost = 'https://api.promptus.ai';
            }
        } else {
            // No context provided, fall back to checking the api_host value
            // This is less reliable as api_host might contain old offline values
            console.warn('[offlineModeHelper] No API context provided, detection may be inaccurate');
            
            const isCloudHost = result.currentHost.includes('api.promptus.ai') || 
                               result.currentHost.includes('api.dev.promptus.ai');
            
            if (!isCloudHost && result.currentHost) {
                result.isOffline = true;
                
                // Determine which server is active based on the current host
                if (result.currentHost === result.serverUrls.gpu) {
                    result.activeServer = 'GPU';
                } else if (result.currentHost === result.serverUrls.cpu) {
                    result.activeServer = 'CPU';
                } else if (result.currentHost === result.serverUrls.cosy) {
                    result.activeServer = 'Cosy';
                } else {
                    // Custom localhost that doesn't match stored values
                    result.activeServer = 'Custom';
                }
            } else {
                result.activeServer = 'Cloud';
            }
        }
        
        return result;
        
    } catch (error) {
        console.error('[offlineModeHelper] Error getting active offline mode:', error);
        return {
            isOffline: false,
            activeServer: 'Cloud',
            currentHost: 'https://api.promptus.ai',
            serverUrls: {
                gpu: 'http://localhost:7111/',
                cpu: 'http://localhost:7112/',
                cosy: 'http://localhost:8190/'
            }
        };
    }
}

/**
 * Gets the active offline server from the API context state
 * @param {Object} apiContext - The PromptusAPIState context object
 * @returns {{
 *   isOffline: boolean,
 *   activeServer: 'GPU' | 'CPU' | 'Cosy' | 'Cloud' | null,
 *   currentHost: string
 * }}
 */
export function getActiveOfflineModeFromContext(apiContext) {
    if (!apiContext) {
        return {
            isOffline: false,
            activeServer: null,
            currentHost: ''
        };
    }
    
    const result = {
        isOffline: !apiContext.online,
        activeServer: null,
        currentHost: apiContext.host || ''
    };
    
    if (!apiContext.online) {
        if (apiContext.activeGPU) {
            result.activeServer = 'GPU';
        } else if (apiContext.activeCPU) {
            result.activeServer = 'CPU';
        } else if (apiContext.activeCosy) {
            result.activeServer = 'Cosy';
        } else {
            result.activeServer = 'Unknown';
        }
    } else {
        result.activeServer = 'Cloud';
    }
    
    return result;
}

/**
 * Checks if a specific server type is currently active
 * @param {'GPU' | 'CPU' | 'Cosy'} serverType - The server type to check
 * @param {Object} apiContext - Optional PromptusAPIState context for accurate detection
 * @returns {Promise<boolean>}
 */
export async function isServerTypeActive(serverType, apiContext = null) {
    const activeMode = await getActiveOfflineMode(apiContext);
    return activeMode.activeServer === serverType;
}

/**
 * Gets the URL for a specific server type
 * @param {'GPU' | 'CPU' | 'Cosy'} serverType - The server type
 * @param {Object} apiContext - Optional PromptusAPIState context for accurate detection
 * @returns {Promise<string>}
 */
export async function getServerUrl(serverType, apiContext = null) {
    const activeMode = await getActiveOfflineMode(apiContext);
    
    switch(serverType) {
        case 'GPU':
            return activeMode.serverUrls.gpu;
        case 'CPU':
            return activeMode.serverUrls.cpu;
        case 'Cosy':
            return activeMode.serverUrls.cosy;
        default:
            return '';
    }
}

/**
 * Checks if the app is currently in offline mode
 * @param {Object} apiContext - Optional PromptusAPIState context for accurate detection
 * @returns {Promise<boolean>}
 */
export async function isOfflineMode(apiContext = null) {
    const activeMode = await getActiveOfflineMode(apiContext);
    return activeMode.isOffline;
}

/**
 * Gets a human-readable status message for the current mode
 * @param {Object} apiContext - Optional PromptusAPIState context for accurate detection
 * @returns {Promise<string>}
 */
export async function getOfflineModeStatus(apiContext = null) {
    const activeMode = await getActiveOfflineMode(apiContext);
    
    if (!activeMode.isOffline) {
        return 'Connected to Promptus Cloud';
    }
    
    switch(activeMode.activeServer) {
        case 'GPU':
            return `Connected to Local GPU Server (${activeMode.currentHost})`;
        case 'CPU':
            return `Connected to Local CPU Server (${activeMode.currentHost})`;
        case 'Cosy':
            return `Connected to Local Cosy Server (${activeMode.currentHost})`;
        case 'Custom':
            return `Connected to Custom Local Server (${activeMode.currentHost})`;
        default:
            return `Offline Mode Active (${activeMode.currentHost})`;
    }
}